import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalService } from 'ui-components/services/modal-service';

import style from './create-customer-modal.module.less';
import { messages } from './messages';
import { AddressFields } from '../../../../address/address-fields';
import { FIELD_NAMES } from '../../../../address/address-fields/constants';
import ModalPortal from '../../../../components/modal-portal';
import { CUSTOMER_CREATED, CUSTOMER_UPDATED } from '../../../../constants/analytics-events';
import { GenericErrorRetryContainer } from '../../../../containers/generic-error-retry-container';
import { Segment } from '../../../../services/analytics-services/segment';
import { isEmailAddressValid } from '../../../../services/email-sanitation/isEmailAddressValid';
import { createCustomerSuccess, updateCustomerSuccess } from '../../../../store/customers/customers.actions';
import * as Api from '../../../../store/customers/customers.api';
import { messages as customerMessages } from '../../../../store/customers/messages';
import { Text } from '../../../../ui-components/components/text/text';
import { TextType } from '../../../../ui-components/components/text/text-type';

const NAME_MIN_LENGTH = 2;
const NAME_CONTAINS_LETTER_REGEX = /[a-zA-Z]/;

const DEFAULT_ADDRESS = {
  name: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: 'US',
  phone: '',
  email: '',
};

const isNameValid = (name) => {
  const trimmedName = name?.trim() ?? '';
  return trimmedName.length >= NAME_MIN_LENGTH && NAME_CONTAINS_LETTER_REGEX.test(trimmedName);
};

/**
 * @param {object} props
 * @param {object} props.modalFrameworkMethods - Modal framework methods
 * @param {'create' | 'edit'} [props.mode] - Modal mode (create or edit)
 * @param {string|null} [props.customerId] - Customer ID for edit mode
 * @param {object | null} [props.initialData] - Initial form data for edit mode
 * @param {string} [props.source] - Source of the modal open (for analytics tracking)
 * @returns {React.ReactElement} The create customer modal component
 */
const CreateCustomerModal = ({
  modalFrameworkMethods,
  mode = 'create',
  customerId = null,
  initialData = null,
  source = 'customers_grid',
}) => {
  const { closeModal } = modalFrameworkMethods;
  const dispatch = useDispatch();
  const intl = useIntl();

  const isEditMode = mode === 'edit';

  const initialAddress = useMemo(
    () =>
      initialData
        ? {
            name: initialData.name,
            company: initialData.company,
            line1: initialData.street1,
            line2: initialData.street2,
            city: initialData.city,
            state: initialData.state,
            postalCode: initialData.postalCode,
            countryCode: initialData.countryCode,
            phone: initialData.phone,
            email: initialData.email,
          }
        : DEFAULT_ADDRESS,
    [initialData],
  );

  const [address, setAddress] = useState(initialAddress);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddressChange = useCallback((propertyName, value) => {
    setAddress((prev) => ({
      ...prev,
      [propertyName]: value,
    }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!address.name?.trim()) {
      newErrors.name = intl.formatMessage(messages.nameRequired);
    } else if (!isNameValid(address.name)) {
      newErrors.name = intl.formatMessage(messages.nameInvalid);
    }
    if (!address.email?.trim()) {
      newErrors.email = intl.formatMessage(messages.emailRequired);
    } else if (!isEmailAddressValid(address.email)) {
      newErrors.email = intl.formatMessage(messages.emailInvalid);
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [address, intl]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const apiPayload = {
      name: address.name,
      email: address.email,
      company: address.company,
      phone: address.phone,
      street1: address.line1,
      street2: address.line2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      countryCode: address.countryCode,
    };

    try {
      if (isEditMode) {
        await Api.updateCustomer(customerId, apiPayload).toPromise();

        const fieldsModified = Object.keys(apiPayload).filter((key) => apiPayload[key] !== initialData?.[key]);

        Segment.trackEvent(CUSTOMER_UPDATED, {
          customerId,
          source,
          fieldsModified,
        });

        dispatch(updateCustomerSuccess());
      } else {
        const response = await Api.createCustomer(apiPayload).toPromise();
        const newCustomerId = response?.data?.customerId;

        Segment.trackEvent(CUSTOMER_CREATED, {
          customerId: newCustomerId,
          source: 'customers_grid',
          fieldsProvided: Object.keys(apiPayload).filter((key) => apiPayload[key] !== null && apiPayload[key] !== ''),
        });

        dispatch(createCustomerSuccess());
      }

      closeModal();
    } catch {
      setIsSubmitting(false);

      ModalService.launchModal({
        component: GenericErrorRetryContainer,
        props: {
          action: () => handleSubmit(),
          title: <Text value={isEditMode ? customerMessages.updateCustomerErrorTitle : customerMessages.createCustomerErrorTitle} />,
          message: <Text value={isEditMode ? customerMessages.updateCustomerErrorMessage : customerMessages.createCustomerErrorMessage} />,
        },
      });
    }
  }, [dispatch, address, validateForm, isEditMode, customerId, isSubmitting, closeModal, source, initialData]);

  const isFormValid = useMemo(() => {
    const hasValidName = isNameValid(address.name);
    const hasEmail = address.email?.trim();
    const isEmailValid = hasEmail && isEmailAddressValid(address.email);
    return hasValidName && isEmailValid;
  }, [address.name, address.email]);

  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return true;
    }
    return Object.keys(initialAddress).some((key) => address[key] !== initialAddress[key]);
  }, [address, initialAddress, isEditMode]);

  return (
    <ModalPortal onClose={closeModal}>
      <Modal className={style['create-customer-modal']} size='medium'>
        <ModalHeader closeModal={closeModal}>
          <Text value={isEditMode ? messages.editTitle : messages.title} />
        </ModalHeader>
        <ModalBody className={style['create-customer-modal-body']}>
          <div className={style['form-section']}>
            <Text textType={TextType.H3} value={messages.customerInfo} />
            <AddressFields
              address={address}
              errors={errors}
              hideOptionalLabels
              isFormDescendant
              onChange={handleAddressChange}
              overrideRequiredFields={[FIELD_NAMES.NAME, FIELD_NAMES.EMAIL]}
              type='v2'
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeModal} theme='tertiary'>
            <Text value={messages.cancel} />
          </Button>
          <Button disabled={!isFormValid || !hasChanges} onClick={handleSubmit} theme='primary'>
            <Text value={isEditMode ? messages.updateCustomer : messages.createCustomer} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

export { CreateCustomerModal };
